import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { Frame, Download } from "lucide-react";

/**
 * Framed Photograph Keepsake — the printable memorial PDF product ($15).
 *
 * Three-page structure (cover · poem · story). Cover uses a fixed portrait
 * "framed photograph" container that renders any scenery (landscape or
 * portrait source) as a beautifully cropped portrait view via CSS
 * `background-image: cover` — the underlying source image in the database is
 * NEVER modified. The crop only exists in this PDF's cover render.
 */
function PdfPage({ children, style = {} }) {
  return (
    <div
      style={{
        width: "816px",
        height: "1056px",
        boxSizing: "border-box",
        background: "linear-gradient(180deg,#FDFBF6 0%,#F5EAD3 100%)",
        color: "#2C2825",
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function PdfFramedPage({ children, style = {} }) {
  return <PdfPage style={style}>{children}</PdfPage>;
}

function PoemPage({ memorial }) {
  const poem = (memorial.poem || "").trim();
  return (
    <PdfPage>
      <div style={{ padding: "88px 96px 40px", textAlign: "center", flex: "1 1 auto", display: "flex", flexDirection: "column" }}>
        <div style={{ letterSpacing: "0.4em", textTransform: "uppercase", color: "#B08A2B", fontSize: "11px", fontFamily: "Arial, sans-serif" }}>
          A verse for {memorial.pet_name}
        </div>
        <div style={{ fontSize: "18px", color: "#B08A2B", marginTop: "12px" }}>✦</div>
        <div
          style={{
            marginTop: "48px",
            flex: "1 1 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <pre
            style={{
              whiteSpace: "pre-wrap",
              margin: 0,
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: "22px",
              lineHeight: 1.8,
              fontStyle: "italic",
              color: "#2C2825",
              textAlign: "center",
              maxWidth: "560px",
            }}
          >
            {poem || "A verse will appear here."}
          </pre>
        </div>
        <div style={{ fontSize: "18px", color: "#B08A2B", marginTop: "36px" }}>✦</div>
      </div>
    </PdfPage>
  );
}

function EulogyPage({ memorial, signature }) {
  const paragraphs = (memorial.eulogy || "").split(/\n\s*\n/).filter(Boolean);
  return (
    <PdfPage>
      <div style={{ padding: "72px 88px 100px", flex: "1 1 auto", display: "flex", flexDirection: "column" }}>
        <div style={{ textAlign: "center", marginBottom: "36px" }}>
          <div style={{ letterSpacing: "0.4em", textTransform: "uppercase", color: "#B08A2B", fontSize: "11px", fontFamily: "Arial, sans-serif" }}>
            Their story
          </div>
          <div
            style={{
              fontSize: "36px",
              fontStyle: "italic",
              color: "#2C2825",
              marginTop: "8px",
              fontFamily: "'Cormorant Garamond', Georgia, serif",
            }}
          >
            {memorial.pet_name}
          </div>
          <div style={{ fontSize: "16px", color: "#B08A2B", marginTop: "6px" }}>✦</div>
        </div>
        <div
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: "13.5px",
            lineHeight: 1.72,
            fontStyle: "italic",
            columnCount: 2,
            columnGap: "36px",
            textAlign: "justify",
            hyphens: "auto",
            flex: "1 1 auto",
            overflow: "hidden",
          }}
        >
          {paragraphs.length === 0 && <p>Their story will appear here.</p>}
          {paragraphs.map((p, i) => (
            <p
              key={i}
              style={{ margin: 0, marginBottom: "12px", breakInside: "avoid" }}
            >
              {p}
            </p>
          ))}
        </div>
      </div>

      {/* Bottom footer row — brand bottom-left, signature bottom-right, same baseline */}
      <div
        style={{
          position: "absolute",
          left: "88px",
          right: "88px",
          bottom: "40px",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: "24px",
        }}
      >
        <div
          style={{
            fontSize: "7.5px",
            letterSpacing: "0.35em",
            textTransform: "uppercase",
            color: "#B8A870",
            fontFamily: "Arial, sans-serif",
            lineHeight: 1,
          }}
        >
          The Heirloom Pet · heirloompet.com
        </div>
        {signature ? (
          <div style={{ textAlign: "right", lineHeight: 1 }}>
            <div style={{ fontSize: "11px", letterSpacing: "0.28em", textTransform: "uppercase", color: "#7A736E", fontFamily: "Arial, sans-serif" }}>
              With love,
            </div>
            <div style={{ fontSize: "22px", fontStyle: "italic", color: "#2C2825", marginTop: "4px", fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              {signature}
            </div>
          </div>
        ) : (
          <div />
        )}
      </div>
    </PdfPage>
  );
}

function FramedCoverPage({ memorial }) {
  const owner = (memorial.owner_display_name || "").trim();
  const dataUri = memorial.scenery_image_base64
    ? `data:${memorial.scenery_mime_type || "image/jpeg"};base64,${memorial.scenery_image_base64}`
    : null;

  // Layout math (validated to avoid any overlap):
  //   top padding    :  56 px
  //   framed image   : 640 px  (5:6.4 portrait aspect, background-image cover)
  //   gap            :  28 px
  //   gold rule      :   1 px
  //   text block     : ~180 px  (paddingTop 20 + caption 12 + gap 10 + name 46 + gap 4 + dates 18 + gap 14 + ornament 16 + gap 10 + owner 11)
  //   safe bottom pad:  48 px minimum
  //   TOTAL          : 56 + 640 + 28 + 1 + 180 + 48 = 953 px  (page = 1056 px → 103 px headroom, no overlap possible)

  return (
    <PdfFramedPage>
      <div style={{ padding: "56px 88px 0", display: "flex", justifyContent: "center" }}>
        {/* Portrait "framed photograph" — landscape sources get cropped to
            portrait for THIS render only. Source image is not modified. */}
        <div
          style={{
            width: "640px",
            height: "640px",
            backgroundImage: dataUri ? `url("${dataUri}")` : "none",
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
            backgroundColor: "#F9F1DC",
            borderRadius: "6px",
            boxShadow:
              "0 20px 40px rgba(212,175,55,0.25), 0 6px 18px rgba(0,0,0,0.08)",
          }}
        />
      </div>

      {/* Thin gold rule */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: "28px" }}>
        <div
          style={{
            width: "160px",
            height: "1px",
            background: "linear-gradient(90deg, transparent, #D4AF37, transparent)",
          }}
        />
      </div>

      {/* Text block — fixed heights, no flex-grow, so nothing can overlap */}
      <div style={{ textAlign: "center", padding: "20px 72px 0" }}>
        <div
          style={{
            letterSpacing: "0.4em",
            textTransform: "uppercase",
            color: "#B08A2B",
            fontSize: "12px",
            fontFamily: "Arial, sans-serif",
            lineHeight: 1,
          }}
        >
          In loving memory
        </div>
        <div
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: "46px",
            fontStyle: "italic",
            color: "#2C2825",
            lineHeight: 1.1,
            marginTop: "10px",
          }}
        >
          {memorial.pet_name || "Their Name"}
        </div>
        {memorial.dates && (
          <div
            style={{
              fontSize: "18px",
              color: "#7A736E",
              fontStyle: "italic",
              marginTop: "4px",
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              lineHeight: 1,
            }}
          >
            {memorial.dates}
          </div>
        )}
        <div style={{ fontSize: "16px", color: "#D4AF37", marginTop: "14px", lineHeight: 1 }}>
          ✦
        </div>
        {owner && (
          <div
            style={{
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              color: "#B08A2B",
              fontSize: "11px",
              fontFamily: "Arial, sans-serif",
              marginTop: "10px",
              lineHeight: 1,
            }}
          >
            — {owner} —
          </div>
        )}
      </div>
    </PdfFramedPage>
  );
}

export default function PdfFramedPhotograph({ memorial }) {
  const params = new URLSearchParams(window.location.search);
  const cameFromPurchase = params.get("pdf_unlocked") === "1";

  const [status, setStatus] = useState(null); // null | 'free' | 'paid' | 'locked'
  const [signature, setSignature] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [priceUsd, setPriceUsd] = useState(15);
  const pageRefs = useRef([]);

  useEffect(() => {
    apiClient
      .get(`/memorials/${memorial.share_slug}/pdf-status`)
      .then(({ data }) => {
        setPriceUsd(data.price_usd || 15);
        if (data.is_free) setStatus("free");
        else if (data.is_paid || cameFromPurchase) setStatus("paid");
        else setStatus("locked");
      })
      .catch(() => setStatus("locked"));
  }, [memorial.share_slug, cameFromPurchase]);

  const startCheckout = async () => {
    setPayBusy(true);
    try {
      const { data } = await apiClient.post("/paypal/checkout/pdf", {
        memorial_slug: memorial.share_slug,
        origin_url: window.location.origin,
      });
      window.location.href = data.approval_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setPayBusy(false);
    }
  };

  const renderAndDownload = async () => {
    setRendering(true);
    try {
      const pdf = new jsPDF({
        unit: "pt",
        format: "letter",
        orientation: "portrait",
        compress: true,
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      for (let i = 0; i < pageRefs.current.length; i++) {
        const el = pageRefs.current[i];
        if (!el) continue;
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(el, {
          scale: 2,
          backgroundColor: null,
          useCORS: true,
          allowTaint: true,
          logging: false,
        });
        const img = canvas.toDataURL("image/jpeg", 0.94);
        if (i > 0) pdf.addPage("letter", "portrait");
        pdf.addImage(img, "JPEG", 0, 0, pageWidth, pageHeight, undefined, "FAST");
      }
      const filename = `${(memorial.pet_name || "keepsake").replace(/\s+/g, "_")}_Framed_Photograph_Keepsake.pdf`;
      pdf.save(filename);
      toast.success("Your framed photograph is downloading ✦");
      setShowModal(false);
    } catch (e) {
      console.error(e);
      toast.error("Could not generate the PDF. Please try again.");
    } finally {
      setRendering(false);
    }
  };

  if (status === null) return null;

  const buttonLabel =
    status === "free"
      ? "Download framed photograph · Free"
      : status === "paid"
      ? "Download your framed photograph ✦"
      : `Printable Memorial Keepsake $15.00`;

  return (
    <>
      <div
        className="glass rounded-sm p-8 max-w-2xl mx-auto"
        data-testid="pdf-framed-photograph-widget"
      >
        <div className="flex items-start gap-3 mb-4">
          <Frame size={22} className="text-[var(--rb-gold)] mt-1 shrink-0" strokeWidth={1.5} />
          <div>
            <div className="tracking-celestial text-[var(--rb-gold)] mb-1">
              Framed Photograph Keepsake
            </div>
            <p className="font-serif-display italic text-[14px] text-[var(--rb-text-body)] leading-relaxed">
              A three-page printable PDF designed as a framed portrait — their scenery presented
              as a beautiful upright photograph, followed by their verse and their story.
              Print at home or your local shop and mount beside their favorite spot.
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            if (status === "locked") startCheckout();
            else setShowModal(true);
          }}
          disabled={payBusy}
          className="btn-gold w-full justify-center disabled:opacity-50"
          data-testid="pdf-framed-photograph-btn"
        >
          {payBusy ? "Opening PayPal…" : buttonLabel}
        </button>
        <p className="text-[11px] text-[var(--rb-text-muted)] italic mt-3 text-center">
          US Letter · 8.5×11" · print-ready at 300 dpi
        </p>
      </div>

      {showModal && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !rendering && setShowModal(false)}
          data-testid="pdf-framed-photograph-modal"
        >
          <div
            className="glass max-w-lg w-full p-8 rounded-sm relative"
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#FDFBF7" }}
          >
            <div className="tracking-celestial text-[var(--rb-gold)] mb-3">One last touch</div>
            <h3 className="font-serif-display text-2xl text-[var(--rb-text-heading)] mb-5">
              A signature for {memorial.pet_name}&apos;s story
            </h3>
            <label className="rb-label">
              Signature (appears at the end of their story, optional)
            </label>
            <input
              type="text"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="e.g. The Smith Family · Mom & Dad · you"
              className="rb-input"
              maxLength={60}
              data-testid="pdf-framed-signature-input"
              autoFocus
            />
            <div className="flex flex-wrap gap-3 mt-6">
              <button
                onClick={renderAndDownload}
                disabled={rendering}
                className="btn-gold disabled:opacity-50"
                data-testid="pdf-framed-download-confirm-btn"
              >
                <Download size={16} className="inline-block mr-2" />
                {rendering ? "Preparing your keepsake…" : "Download PDF"}
              </button>
              <button
                onClick={() => setShowModal(false)}
                disabled={rendering}
                className="btn-ghost"
              >
                Cancel
              </button>
            </div>
            <p className="text-[10px] text-[var(--rb-text-muted)] italic mt-4">
              This may take 5–10 seconds. Your download starts automatically.
            </p>
          </div>
        </div>
      )}

      {showModal &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              left: "-10000px",
              top: 0,
              zIndex: -1,
              pointerEvents: "none",
            }}
            aria-hidden="true"
          >
            <div ref={(el) => (pageRefs.current[0] = el)}>
              <FramedCoverPage memorial={memorial} />
            </div>
            <div ref={(el) => (pageRefs.current[1] = el)}>
              <PoemPage memorial={memorial} />
            </div>
            <div ref={(el) => (pageRefs.current[2] = el)}>
              <EulogyPage memorial={memorial} signature={signature} />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
